import { ApiBounds } from "../../shared/interfaces";
import * as UIExtension from "@foxitsoftware/foxit-pdf-sdk-for-web-library/lib/UIExtension.full.js";

import { getAnnotationContainerForPage } from "./annotationRenderer";
import { PrimeDebugLog } from "../../util/debugLog";

const MIN_ANNOTATION_SIDE_SIZE = 0.3;
const MAX_ANNOTATION_SIDE_SIZE = 0.9;
const MAX_ZOOM_LEVEL = 1.5;
const PCT_ANNOTATION_MARGIN = 0.1;
const MIN_ANNOTATION_VERTICAL_MARGIN_PX = 16;

/**
 * Adjusts the zoom level and scrolls the viewer so that the specified bounds are visible.
 *
 * @param pdfUi - An instance of the PDFUI.
 * @param bounds - The bounds of the area to make visible.
 * @param adjustZoom Whether to change the zoom level.
 * @param isMultiPage - Are the bounds only part of a multipage annotation?
 */
export async function makeBoundsVisible(
    pdfUi: UIExtension.PDFUI,
    bounds: ApiBounds,
    adjustZoom: boolean,
    isMultiPage: boolean,
): Promise<void> {
    PrimeDebugLog("FileViewer", `makeBoundsVisible START ${pdfUi} ${bounds} ${isMultiPage}`);
    if (pdfUi == null) {
        return;
    }

    // Get the container for the page containing the bounds.
    const annotContainerForPage = getAnnotationContainerForPage(bounds.page);
    const annotContainerRect = annotContainerForPage?.getBoundingClientRect();

    if (!annotContainerRect || !annotContainerForPage) {
        PrimeDebugLog("FileViewer", `makeBoundsVisible END missing annotation rect`);
        return;
    }

    const scrollWrap = await pdfUi.getScrollWrap();

    if (adjustZoom) {
        // STEP 1: Adjust the zoom level so the annotation has a good size on screen.
        const pctPageVisible = Math.min(
            scrollWrap.getWidth() / annotContainerRect.width,
            scrollWrap.getHeight() / annotContainerRect.height,
        );

        const annotationLongestSide = Math.max(
            bounds.xMax - bounds.xMin,
            bounds.yMax - bounds.yMin,
        );
        if (annotationLongestSide === 0) {
            PrimeDebugLog("FileViewer", "Annotation has no size.");
            return;
        }

        const annotationLongestSidePctPageVisible = (1 / pctPageVisible) * annotationLongestSide;

        const annotationSizeOutsideBounds =
            annotationLongestSidePctPageVisible < MIN_ANNOTATION_SIDE_SIZE ||
            annotationLongestSidePctPageVisible > MAX_ANNOTATION_SIDE_SIZE;
        if (annotationSizeOutsideBounds) {
            const pageRenderer = await pdfUi.getPDFPageRender(bounds.page);
            if (!pageRenderer) {
                PrimeDebugLog("FileViewer", "Page renderer not found.");
                return;
            }

            const pdfScale = await pageRenderer.getScale();
            const marginAdjustment = 1 - 2 * PCT_ANNOTATION_MARGIN;
            const newZoomLevel =
                pdfScale * (1 / annotationLongestSide) * pctPageVisible * marginAdjustment;
            const clampedZoomLevel = Math.min(newZoomLevel, MAX_ZOOM_LEVEL);
            try {
                await pdfUi.zoomTo(clampedZoomLevel);
            } catch (e) {
                console.error(e);
            }
        }
    }

    const pdfDocRender = await pdfUi.getPDFDocRender();
    const scrollHost = scrollWrap?.getScrollHost() as HTMLElement;
    const scrollBehavior = "instant" as ScrollBehavior;

    // STEP 2: Go to the correct page.
    try {
        const currentPage = pdfDocRender?.getCurrentPageIndex();
        PrimeDebugLog(
            "FileViewer",
            `going to page start  | current page ${currentPage} | target page ${bounds.page}`,
        );
        await pdfDocRender?.goToPage(bounds.page, { x: 0, y: 0 });
        PrimeDebugLog("FileViewer", "going to page done", bounds.page);
    } catch (e) {
        console.error(e);
    }

    // STEP 3: Scroll the container so that the bounds are visible.
    const scrollHostRect = scrollHost.getBoundingClientRect();
    const annotContainerRectAfterScale = annotContainerForPage.getBoundingClientRect();
    const annotWidth = annotContainerRectAfterScale.width * (bounds.xMax - bounds.xMin);
    const annotHeight = annotContainerRectAfterScale.height * (bounds.yMax - bounds.yMin);

    const isVisibleInViewport =
        annotContainerRectAfterScale.bottom > 0 &&
        annotContainerRectAfterScale.top < window.innerHeight &&
        annotContainerRectAfterScale.right > 0 &&
        annotContainerRectAfterScale.left < window.innerWidth;

    PrimeDebugLog(
        "FileViewer",
        `makeBoundsVisible | annotContainerRectAfterScale | scrollHostRect | isVisibleInViewport`,
        annotContainerRectAfterScale,
        scrollHostRect,
        isVisibleInViewport,
    );

    // Determine vertical margin – center the annotation if possible.
    const annotationVerticalMargin = isMultiPage
        ? MIN_ANNOTATION_VERTICAL_MARGIN_PX
        : (scrollHostRect.height - annotHeight) / 2;
    const dy = bounds.yMin * annotContainerRectAfterScale.height - annotationVerticalMargin;
    const dx =
        bounds.xMin * annotContainerRectAfterScale.width - (scrollHostRect.width - annotWidth) / 2;

    // Workaround -- Typescript definitions are wrong -- does not contain "instant"
    scrollHost.scrollBy({ left: dx, top: dy, behavior: scrollBehavior });

    // Make sure we actually are at the page
    const scrollEvent = new WheelEvent("wheel", {
        bubbles: true,
        cancelable: true,
        deltaY: 100, // Positive to scroll down, negative to scroll up
    });

    annotContainerForPage.dispatchEvent(scrollEvent);

    PrimeDebugLog("FileViewer", `makeBoundsVisible END ${bounds} ${isMultiPage}`);
}
